import { motion } from 'framer-motion';
import { useInView } from '../../../hooks/useInView';

export default function ResearchProject() {
  const { ref, isInView } = useInView({ threshold: 0.1 });

  return (
    <section ref={ref} className="relative py-16 sm:py-24 lg:py-32 bg-[#050505]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 1 }}
          className="text-center mb-10 sm:mb-16 lg:mb-20"
        >
          <span
            className="text-[#00E5FF] text-xs sm:text-sm tracking-[0.3em] uppercase font-semibold"
            style={{ fontFamily: 'Orbitron, sans-serif' }}
          >
            Innowacje B+R
          </span>
          <h2
            className="text-2xl sm:text-4xl lg:text-5xl font-bold text-white mt-3 sm:mt-4 leading-tight"
            style={{ fontFamily: 'Rajdhani, sans-serif' }}
          >
            Projekt <span className="text-[#00E5FF]">Badawczy</span>
          </h2>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 1, delay: 0.3 }}
          className="grid lg:grid-cols-2 gap-8 sm:gap-12 lg:gap-16 items-center"
        >
          <div>
            <div className="relative group">
              <img
                src="https://public.readdy.ai/ai/img_res/55a25f77245b72d73193188928b97d45.jpg"
                alt="Innowacyjny mostek rowerowy"
                className="w-full h-[250px] sm:h-[350px] lg:h-[500px] object-cover object-center"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-transparent to-transparent"></div>
              <div className="absolute top-4 left-4 sm:top-8 sm:left-8 w-14 h-14 sm:w-20 sm:h-20 flex items-center justify-center bg-[#00E5FF]/90 border-2 border-white">
                <i className="ri-lightbulb-line text-2xl sm:text-4xl text-white"></i>
              </div>
            </div>
          </div>

          <div>
            <h3
              className="text-xl sm:text-2xl lg:text-3xl font-bold text-white mb-4 sm:mb-6 leading-tight"
              style={{ fontFamily: 'Rajdhani, sans-serif' }}
            >
              Innowacyjny Mostek Rowerowy z Tłumieniem Drgań
            </h3>

            <p
              className="text-[#C0C7CF] text-sm sm:text-base leading-relaxed mb-5 sm:mb-8"
              style={{ fontFamily: 'Inter, sans-serif' }}
            >
              Opracowanie i przetestowanie w toku prac B+R innowacyjnego mostka
              rowerowego wyposażonego w elastomer umożliwiający tłumienie
              szkodliwych drgań przenoszonych z przedniego koła na kierownicę.
            </p>

            <div className="space-y-4 sm:space-y-6 mb-6 sm:mb-8">
              <div className="bg-[#111111] border border-[#00E5FF]/20 p-4 sm:p-6">
                <h4
                  className="text-[#00E5FF] font-bold text-base sm:text-lg mb-2 sm:mb-3"
                  style={{ fontFamily: 'Orbitron, sans-serif' }}
                >
                  <a
                    href="#cel-projektu-br"
                    className="hover:text-white transition-colors cursor-pointer"
                  >
                    Cel Projektu
                  </a>
                </h4>
                <p
                  className="text-[#C0C7CF] text-xs sm:text-sm leading-relaxed"
                  style={{ fontFamily: 'Inter, sans-serif' }}
                >
                  Podniesienie potencjału oraz konkurencyjności poprzez
                  opracowanie oraz wdrożenie innowacyjnego produktu w postaci
                  mostka rowerowego wyposażonego w tuleje elastomerowe
                  umożliwiające tłumienie szkodliwych dla zdrowia drgań.
                </p>
              </div>

              <div className="bg-[#111111] border border-[#00E5FF]/20 p-4 sm:p-6">
                <h4
                  className="text-[#00E5FF] font-bold text-base sm:text-lg mb-2 sm:mb-3"
                  style={{ fontFamily: 'Orbitron, sans-serif' }}
                >
                  <a
                    href="#efekty-projektu-br"
                    className="hover:text-white transition-colors cursor-pointer"
                  >
                    Efekty Projektu
                  </a>
                </h4>
                <div className="space-y-2 sm:space-y-3">
                  <div className="flex items-start gap-2 sm:gap-3">
                    <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-[#00E5FF] mt-1.5 sm:mt-2 flex-shrink-0"></div>
                    <p
                      className="text-[#C0C7CF] text-xs sm:text-sm"
                      style={{ fontFamily: 'Inter, sans-serif' }}
                    >
                      Opracowanie projektu i prototypu nowego mostka z tulejami
                      elastomerowymi
                    </p>
                  </div>
                  <div className="flex items-start gap-2 sm:gap-3">
                    <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-[#00E5FF] mt-1.5 sm:mt-2 flex-shrink-0"></div>
                    <p
                      className="text-[#C0C7CF] text-xs sm:text-sm"
                      style={{ fontFamily: 'Inter, sans-serif' }}
                    >
                      Laboratoryjne określenie skuteczności tłumienia drgań
                    </p>
                  </div>
                  <div className="flex items-start gap-2 sm:gap-3">
                    <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-[#00E5FF] mt-1.5 sm:mt-2 flex-shrink-0"></div>
                    <p
                      className="text-[#C0C7CF] text-xs sm:text-sm"
                      style={{ fontFamily: 'Inter, sans-serif' }}
                    >
                      Testowanie i optymalizacja w warunkach rzeczywistych
                    </p>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3 sm:gap-4">
                <div className="bg-[#111111] border border-[#00E5FF]/20 p-4 sm:p-6 text-center">
                  <div
                    className="text-lg sm:text-2xl lg:text-3xl font-bold text-[#00E5FF] mb-1 sm:mb-2"
                    style={{ fontFamily: 'Orbitron, sans-serif' }}
                  >
                    525 243 PLN
                  </div>
                  <div
                    className="text-[#C0C7CF] text-[10px] sm:text-xs"
                    style={{ fontFamily: 'Inter, sans-serif' }}
                  >
                    Wartość projektu
                  </div>
                </div>
                <div className="bg-[#111111] border border-[#00E5FF]/20 p-4 sm:p-6 text-center">
                  <div
                    className="text-lg sm:text-2xl lg:text-3xl font-bold text-[#00E5FF] mb-1 sm:mb-2"
                    style={{ fontFamily: 'Orbitron, sans-serif' }}
                  >
                    340 000 PLN
                  </div>
                  <div
                    className="text-[#C0C7CF] text-[10px] sm:text-xs"
                    style={{ fontFamily: 'Inter, sans-serif' }}
                  >
                    Dofinansowanie
                  </div>
                </div>
              </div>

              <div className="bg-white p-3 sm:p-6 flex items-center justify-center">
                <img
                  src="https://static.readdy.ai/image/3af96b06175dd96fb400bc83db1e5fde/59dd77489edcebf11d8b56555cee8b23.jpeg"
                  alt="Fundusze Europejskie - Rzeczpospolita Polska - Unia Europejska"
                  className="w-full max-w-2xl h-auto object-contain"
                />
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
